import { useEffect, useMemo, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { Cliente, StatoContenuto } from '../types';
import { appuntamentoDelCliente, valutaRiprese, URL_APPUNTAMENTI_RIPRESE } from '../utils/ripreseMatch';
import { contenutiDaScrivere } from '../utils/contenutiDaScrivere';
import { reelRegistratiDaProgrammare } from '../utils/reelRegistrati';
import { daMontare, reelMontatiNelMese } from '../utils/editing';
import { isPost } from '../utils/tipoContenuto';
import { programmatoOUscito } from '../utils/pianoUscite';

// Scheda di un cliente attivo per il mese corrente: tutti i numeri delle card
// di "Da gestire" e le condizioni dei suoi avvisi. È l'UNICA fonte per la
// pagina Da gestire e per "Maggiori dettagli": le due pagine leggono lo stesso
// oggetto, quindi non possono mostrare numeri diversi.
export interface SchedaCliente {
  cliente: Cliente | { id: string; nomeAzienda: string; logoUrl?: string };
  clienteId: string;
  testiDalCliente: boolean;
  // Righe delle card, nell'ordine in cui compaiono
  daScrivere: number;
  daRegistrare: number;
  /** Reel girati nel mese, programmati o meno (badge "Registrati", come Riprese) */
  reelGiaGirati: number;
  /** Registrati e non ancora montati, anche se già programmati (come Editing) */
  daMontare: number;
  /** Montati nel mese, programmati o meno (come Editing) */
  montati: number;
  /** Programmati/pubblicati con data nel mese */
  programmati: number;
  reelMensili: number;
  progresso: number;
  obiettivoRaggiunto: boolean;
  mancantiAlTarget: number;
  /** Registrati non ancora programmati: alimenta il promemoria "programmali ora" */
  registratiNonProgrammati: number;
  attesoAdOggi: number;
  // I tre avvisi in cima alla card
  occorronoRiprese: boolean;
  mancanoTesti: boolean;
  daProgrammareOra: boolean;
}

export interface SchedeClienti {
  schede: SchedaCliente[];
  /** Vero quando appuntamenti e stats sono arrivati: gli avvisi valutati su
      dati parziali lampeggerebbero (vedi Dashboard, fix 16/07/2026). */
  alertDataPronti: boolean;
  profiliRiprese: number;
  profiliTesti: number;
  profiliDaProgrammare: number;
  /** Somme sui clienti attivi, per i riepiloghi */
  totali: {
    daScrivere: number;
    daRegistrare: number;
    registrati: number;
    daMontare: number;
    montati: number;
    programmati: number;
    reelMensili: number;
  };
}

export const useSchedeClienti = (): SchedeClienti => {
  const { state, loadDashboardStats } = useData();
  const [titoliAppuntamenti, setTitoliAppuntamenti] = useState<string[]>([]);
  const [appuntamentiCaricati, setAppuntamentiCaricati] = useState(false);
  const [statsCaricate, setStatsCaricate] = useState(false);

  // Appuntamenti riprese dei videomaker (calendario iCloud live): servono per
  // sopprimere "Occorrono riprese" quando sono già fissate e per segnalare
  // "mancano i testi" quando sono fissate ma non c'è nulla da girare.
  useEffect(() => {
    fetch(URL_APPUNTAMENTI_RIPRESE, {
      headers: { Authorization: `Bearer ${localStorage.getItem('bymarke_token')}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (j?.success) setTitoliAppuntamenti((j.data.appuntamenti || []).map((a: any) => a.titolo));
      })
      .catch(() => {})
      .finally(() => setAppuntamentiCaricati(true));
  }, []);

  useEffect(() => {
    loadDashboardStats()
      .catch((error) => console.error('Error loading dashboard stats:', error))
      .finally(() => setStatsCaricate(true));
  }, []);

  const stats = state.dashboardStats;

  return useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentDay = now.getDate();
    const giorniNelMese = new Date(currentYear, currentMonth + 1, 0).getDate();

    const schede: SchedaCliente[] = [...(stats?.client_progress || state.clienti.filter(c => c.status === 'active'))]
      .sort((a: any, b: any) =>
        (a.company_name || a.nomeAzienda || '').localeCompare(b.company_name || b.nomeAzienda || '', 'it', { sensitivity: 'base' })
      )
      .map((clienteData: any) => {
        const cliente = stats?.client_progress
          ? state.clienti.find(c => c.id === clienteData.client_id) || { id: clienteData.client_id, nomeAzienda: clienteData.company_name }
          : clienteData;
        const clienteId: string = stats?.client_progress ? clienteData.client_id : cliente.id;
        const contenutiDelCliente = state.contenuti.filter(c => c.clienteId === clienteId);

        // Se i testi li scrive il cliente, per noi non c'è niente da scrivere.
        const testiDalCliente = !!(cliente as any).testiDalCliente;

        let programmati: number, reelMensili: number;
        if (stats?.client_progress) {
          programmati = clienteData.completed_reels;
          reelMensili = clienteData.target_reels;
        } else {
          programmati = contenutiDelCliente.filter(c => {
            if (isPost(c) || !c.dataPubblicazione) return false;
            const d = new Date(c.dataPubblicazione);
            return d.getMonth() === currentMonth && d.getFullYear() === currentYear && programmatoOUscito(c);
          }).length;
          reelMensili = cliente.reelMensili || 0;
        }

        // Da scrivere: calcolo condiviso con la pagina Copywriting
        const daScrivere = contenutiDaScrivere({
          contenuti: state.contenuti,
          clienteId,
          targetMensile: reelMensili,
          testiDalCliente,
          mese: currentMonth,
          anno: currentYear,
        });

        // Da registrare: stessi contenuti della coda della pagina Riprese
        const daRegistrare = contenutiDelCliente.filter(c =>
          !isPost(c) &&
          !c.videoRegistrato &&
          (c.stato === StatoContenuto.TESTO_PRONTO ||
           c.stato === StatoContenuto.MONTATO ||
           c.stato === StatoContenuto.PROGRAMMATO)
        ).length;

        // Registrati non ancora programmati: promemoria "programmali ora" e
        // controllo "mancano i testi" (non è il badge Registrati).
        const registratiNonProgrammati = reelRegistratiDaProgrammare(state.contenuti, clienteId);

        // Editing: stessi predicati delle card della pagina Editing
        const daMontareCliente = contenutiDelCliente.filter(daMontare).length;
        const montati = reelMontatiNelMese(contenutiDelCliente, currentMonth, currentYear).length;

        const progresso = reelMensili > 0 ? (programmati / reelMensili) * 100 : 0;
        const obiettivoRaggiunto = programmati >= reelMensili && reelMensili > 0;
        const mancantiAlTarget = Math.max(0, reelMensili - programmati);

        // Sollecito riprese: logica unica con la pagina Riprese (valutaRiprese).
        const { reelGiaGirati, attesoAdOggi, inRitardoRiprese } = valutaRiprese(
          contenutiDelCliente, reelMensili, currentMonth, currentYear, currentDay, giorniNelMese,
        );
        // Riprese già in agenda: niente sollecito.
        const nomiAttivi = state.clienti.filter((c) => c.status === 'active').map((c) => c.nomeAzienda || '');
        const ripreseFissate = titoliAppuntamenti.some((t) =>
          appuntamentoDelCliente(cliente.nomeAzienda || '', t, nomiAttivi)
        );
        // Riprese fissate ma pipeline vuota: il copywriter deve ancora scrivere.
        // Non vale se i testi li scrive il cliente o se il mese è già chiuso.
        const mancanoTesti =
          !testiDalCliente &&
          ripreseFissate &&
          daRegistrare === 0 &&
          registratiNonProgrammati === 0 &&
          !obiettivoRaggiunto;
        // I registrati in attesa bastano a chiudere il mese: vanno solo programmati.
        const daProgrammareOra = mancantiAlTarget > 0 && registratiNonProgrammati >= mancantiAlTarget;

        return {
          cliente, clienteId, testiDalCliente,
          daScrivere, daRegistrare, reelGiaGirati, daMontare: daMontareCliente, montati, programmati,
          reelMensili, progresso, obiettivoRaggiunto, mancantiAlTarget, registratiNonProgrammati, attesoAdOggi,
          occorronoRiprese: inRitardoRiprese && !ripreseFissate,
          mancanoTesti, daProgrammareOra,
        };
      });

    const somma = (f: (s: SchedaCliente) => number) => schede.reduce((t, s) => t + f(s), 0);

    return {
      schede,
      alertDataPronti: appuntamentiCaricati && statsCaricate,
      profiliRiprese: schede.filter(s => s.occorronoRiprese).length,
      profiliTesti: schede.filter(s => s.mancanoTesti).length,
      profiliDaProgrammare: schede.filter(s => s.daProgrammareOra).length,
      totali: {
        daScrivere: somma(s => s.daScrivere),
        daRegistrare: somma(s => s.daRegistrare),
        registrati: somma(s => s.reelGiaGirati),
        daMontare: somma(s => s.daMontare),
        montati: somma(s => s.montati),
        programmati: somma(s => s.programmati),
        reelMensili: somma(s => s.reelMensili),
      },
    };
  }, [state.clienti, state.contenuti, stats, titoliAppuntamenti, appuntamentiCaricati, statsCaricate]);
};
